import React, { useMemo } from 'react';
import { DesktopRouteLayer } from './DesktopRouteLayer.js';
import { MobileRouteLayer } from './MobileRouteLayer.js';
import type { Position } from '../../types.js';

interface AdaptiveRouteLayerProps {
  route: Position[];
  onHover: (index: number | null) => void;
  setSnapPoint: (point: Position | null) => void;
  onRouteRendered?: () => void; // Callback when route is fully rendered
  highlightedSurface?: string | null; // Surface type to highlight
  routeSegments?: any[]; // Route segments for surface highlighting
}

/**
 * Adaptive route layer that automatically chooses the best rendering strategy
 * (Desktop or Mobile) for continuous line display across all devices.
 * Uses device detection and route complexity analysis for optimal performance.
 */
export const AdaptiveRouteLayer: React.FC<AdaptiveRouteLayerProps> = (props) => {
  const { deviceStrategy } = useMemo(() => {
    // Detect device capabilities
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobileUA = /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;
    const isLowPowerDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
    
    const isMobile = isMobileUA || (isTouchDevice && isSmallScreen);
    const routeLength = props.route?.length || 0;
    
    // Determine rendering strategy
    let strategy: 'simple' | 'direct' = 'simple';
    
    // Use mobile-optimized rendering for:
    // 1. Mobile devices (better line continuity on touch devices)
    // 2. Complex routes on any device (better canvas performance)
    // 3. Low-power devices with many points (canvas optimization)
    if (isMobile || routeLength > 1000 || (isLowPowerDevice && routeLength > 500)) {
      strategy = 'direct';
    }
    
    return {
      deviceStrategy: strategy
    };
  }, [props.route?.length]);

  // Extract props for route layers (excluding surface highlighting for now)
  const routeLayerProps = {
    route: props.route,
    onHover: props.onHover,
    setSnapPoint: props.setSnapPoint,
    onRouteRendered: props.onRouteRendered
  };

  // Render with optimal strategy
  switch (deviceStrategy) {
    case 'direct':
      return <MobileRouteLayer {...routeLayerProps} />;
    case 'simple':
    default:
      return <DesktopRouteLayer {...routeLayerProps} />;
  }
};
